/**
 * Emissions Admin Page
 *
 * Admin settings page with card-based navigation for emissions tracker.
 * Uses AdminSettings component for consistent layout with other apps.
 */

import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AdminSettingSection, AdminSettings } from '@features/admin-settings/v2';
import { generateUrl } from '@routes/util';
import { useAppSelector } from '../../../reducers';
import { isStaff as isStaffSelector } from '../../../selectors/user';
import { useSeedSampleDataMutation } from '../api/emissions';
import { emissionsTrackerRoutes } from '../routes';

export const EmissionsAdminPage = () => {
  const history = useHistory();
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const isStaff = useAppSelector(isStaffSelector);
  const [seedSampleData] = useSeedSampleDataMutation();
  const [isSeedingData, setIsSeedingData] = useState(false);

  const handleSeedSampleData = async () => {
    setIsSeedingData(true);
    try {
      await seedSampleData({ initiativeId, includeChildren: true });
    } finally {
      setIsSeedingData(false);
    }
  };

  const sections: AdminSettingSection[] = [
    {
      label: 'Data Configuration',
      cards: [
        {
          icon: 'fal fa-leaf',
          text: 'Emission Factors',
          clickHandler: () => history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_FACTORS, { initiativeId })),
          description: 'Browse and manage emission factors from EPA, DEFRA, IEA, and custom sources.',
        },
        {
          icon: 'fal fa-chart-pie',
          text: 'Business Metrics',
          clickHandler: () =>
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_BUSINESS_METRICS, { initiativeId })),
          description: 'Configure employee count, revenue, and floor area for carbon intensity calculations.',
        },
        {
          icon: 'fal fa-cog',
          text: 'Settings',
          clickHandler: () => history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_SETTINGS, { initiativeId })),
          description: 'Configure GWP methodology, reporting boundaries, base year, and display preferences.',
        },
        {
          icon: 'fal fa-truck-loading',
          text: 'Manage Suppliers',
          description: 'Track suppliers and their emission factors for Scope 3 supply chain reporting.',
          disabledText: 'Coming soon',
        },
      ],
    },
    {
      label: 'Data Management',
      cards: [
        {
          icon: 'fal fa-history',
          text: 'Import History',
          clickHandler: () => history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_IMPORT_HISTORY, { initiativeId })),
          description: 'View past bulk imports, download original files, and undo imports.',
        },
        {
          icon: 'fal fa-paperclip',
          text: 'Evidence Library',
          clickHandler: () =>
            history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_EVIDENCE_LIBRARY, { initiativeId })),
          description: 'Browse and manage all uploaded evidence documents linked to activities.',
        },
        {
          icon: 'fal fa-file-export',
          text: 'Data Export',
          clickHandler: () => history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_DATA_EXPORT, { initiativeId })),
          description: 'Export emissions data and calculation audit trails.',
        },
        {
          icon: 'fal fa-badge-check',
          text: 'Data Quality',
          description: 'Identify low-quality data points and take action to improve accuracy.',
          disabledText: 'Coming soon',
        },
      ],
    },
    {
      label: 'User & Platform Management',
      cards: [
        {
          icon: 'fal fa-users-gear',
          text: 'Manage Users',
          clickHandler: () => history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_MANAGE_USERS, { initiativeId })),
          description: 'Invite, manage, and remove users along with their access and delegation permissions.',
        },
      ],
    },
    {
      label: 'Staff Tools',
      cards: [
        {
          icon: 'fal fa-flask',
          text: isSeedingData ? 'Seeding...' : 'Re-seed Sample Data',
          clickHandler: handleSeedSampleData,
          description: 'Deprecated — Re-seed sample activity data including children. For internal testing only.',
          disabledText: isSeedingData ? 'Seeding in progress...' : undefined,
          isHidden: !isStaff,
        },
      ],
    },
  ];

  return <AdminSettings sections={sections} />;
};
